import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useCookieConsent } from '../contexts/CookieConsentContext'

declare global {
  interface Window {
    _paq: Array<[string, ...unknown[]]>
  }
}

const MatomoAnalytics = () => {
  const location = useLocation()
  const { preferences, hasConsent } = useCookieConsent()

  useEffect(() => {
    // Only initialize if user has consented to analytics
    if (!hasConsent || !preferences?.analytics) {
      return
    }

    // Skip if already initialized (for React StrictMode)
    if (window._paq && window._paq.length > 0) {
      return
    }

    // Initialize Matomo tracking
    window._paq = window._paq || []

    // Configuration from environment variables
    const matomoUrl = import.meta.env.VITE_MATOMO_URL || 'https://analytics.demirix.dev'
    const siteId = import.meta.env.VITE_MATOMO_SITE_ID || '5'

    window._paq.push(['setTrackerUrl', `${matomoUrl}/matomo.php`])
    window._paq.push(['setSiteId', siteId])
    window._paq.push(['enableLinkTracking'])
    window._paq.push(['setDoNotTrack', true])
    window._paq.push(['disableCookies']) // GDPR compliant - no cookies

    // Check if script already exists
    const scriptSrc = `${matomoUrl}/matomo.js`
    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`)
    if (!existingScript) {
      // Load Matomo script
      const script = document.createElement('script')
      script.async = true
      script.src = scriptSrc
      document.head.appendChild(script)
    }
  }, [hasConsent, preferences])

  useEffect(() => {
    // Only track page views if analytics consent is given
    if (!hasConsent || !preferences?.analytics) {
      return
    }

    // Track page views on route changes
    if (window._paq) {
      window._paq.push(['setCustomUrl', location.pathname + location.search])
      window._paq.push(['setDocumentTitle', document.title])
      window._paq.push(['trackPageView'])
    }
  }, [location, hasConsent, preferences])

  return null
}

export default MatomoAnalytics
